import WithAuth from "../../hoc/WithAuth";

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { DataTable } from "../../components/ui/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Eye } from "lucide-react";
import AdminProductModal from "./AdminProductModal";
import { useAuth } from "../../providers/AuthProvider";
import { useProductList } from "../../api/product";
import { AppButton } from "../../components/ui/AppButton";
import { Icons } from "../../assets/images/icons";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";

/** Define Product type */
interface Product {
  id: string;
  productName: string;
  category: string;
  variety: string;
  unitOfMeasurementId?: number;
  unitOfMeasurementName: string;
}

interface AuthContextValue {
  user: any;
}

function ProductsPage() {
  const navigate = useNavigate();
  const { user } = useAuth() as AuthContextValue;

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  /** API */
  const { data: products, isLoading } = useProductList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedProducts = useMemo((): Product[] => {
    if (!products?.data?.content) return [];

    return products.data.content.map((item: any) => ({
      id: item.id,
      productName: item.productName || "N/A",
      category: item.category || "N/A",
      variety: item.variety || "N/A",
      unitOfMeasurementId: item.unitOfMeasurementId,
      unitOfMeasurementName: item.unitOfMeasurementName || "N/A",
    }));
  }, [products]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleCreate = () => {
    setSelectedProductId("");
    setModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProductId(product.id);
    setModalOpen(true);
  };

  const handleRowClick = (product: Product) => {
    if (user.roleName === "ROLE_KFT_SUPER_ADMIN")
      navigate(`/agronomy/products/${product.id}`);
    else if (user.roleName === "ROLE_OFF_TAKER_ADMIN")
      navigate(`/products/${product.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const hideModal = () => {
    setModalOpen(false);
    setSelectedProductId("");
  };

  /** Define columns for the DataTable */
  const productColumns = [
    {
      key: "category",
      title: "Category",
      dataIndex: "category",
      align: "left" as const,
    },
    {
      key: "productName",
      title: "Product Name",
      dataIndex: "productName",
      align: "left" as const,
    },
    {
      key: "variety",
      title: "Variety",
      dataIndex: "variety",
      align: "left" as const,
    },
    {
      key: "unitOfMeasurementName",
      title: "Unit",
      dataIndex: "unitOfMeasurementName",
      align: "left" as const,
    },
    ...(user?.roleName === "ROLE_KFT_SUPER_ADMIN"
      ? [
          {
            key: "actions",
            title: "Actions",
            align: "center" as const,
            render: (_: any, record: Product) => {
              return (
                <Button
                  variant="default"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProduct(record);
                  }}
                >
                  <img
                    src={Icons.pencilEdit}
                    alt="edit"
                    className="h-4 w-4 text-gray-100 mr-1"
                  />
                  Edit
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  /** Mobile view component (kept here since it's simple) */
  const MobileProductsView = () => (
    <div className="w-full divide-y divide-gray-200 md:hidden px-4 mb-4">
      <div className="py-4 sticky top-0 bg-background z-10">
        <Input
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search products..."
          className="bg-muted"
        />
      </div>

      {transformedProducts?.map((item: Product) => (
        <div
          key={item.id}
          className="py-4 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => handleRowClick(item)}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <div className="font-semibold text-lg text-foreground">
                {item.productName}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(item);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProduct(item);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              {item.category} - {item.variety}
            </div>

            <div className="flex justify-between items-center">
              <div className="font-medium text-foreground">
                Unit: {item.unitOfMeasurementName}
              </div>
            </div>
          </div>
        </div>
      ))}

      {transformedProducts.length === 0 && !isLoading && (
        <div className="text-center py-8 text-muted-foreground">
          No products found
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="py-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      )}

      {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={handleCreate}
            size="lg"
            className="rounded-full h-14 w-14 shadow-lg bg-green-500 hover:bg-green-600 text-white"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="bg-transparent lg:bg-white p-4 lg:p-6 h-screen"
      style={{
        borderRadius: 8.5,
        marginTop: 25,
      }}
    >
      {/** Header */}
      <div className="flex flex-col sm:flex-row justify-between">
        {/** Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search products by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(productColumns, transformedProducts, "Product Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={productColumns}
            data={transformedProducts}
            fileName="products"
            title="Product Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: products?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
          {/** Create Product Button - Only for admin */}
          {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
            <AppButton
              variant="success"
              customIcon={Icons.addCircle}
              onClick={handleCreate}
            >
              Create New Product
            </AppButton>
          )}
        </div>
      </div>

      {/** Desktop Table View */}
      <div className="hidden md:block">
        <DataTable<Product>
          data={transformedProducts}
          columns={productColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: products?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No products found"
          rowKey="id"
        />
      </div>

      {/** Mobile View */}
      <MobileProductsView />

      {/** Modal */}
      <AdminProductModal
        open={modalOpen}
        selectedId={selectedProductId}
        hideModal={hideModal}
      />
    </div>
  );
}

export default WithAuth(ProductsPage);
